/* eslint-disable no-underscore-dangle */
/**
 * Align webpack lazy-chunk base URL with the guest app's asset root.
 * Guest apps may set window.emui._ASSET_PATH during bootstrap; otherwise
 * the hostUrl supplied to ScriptLoader is used.
 */
type WindowWithWebpackPublicPath = Window & {
  __webpack_public_path__?: string;
};

/**
 * @param {Window | null | undefined} [targetWindow] - Guest document defaultView whose webpack runtime should be synced.
 */
export const syncWebpackPublicPathFromAssetPath = (
  targetWindow?: Window | null,
): void => {
  if (!targetWindow) return;

  const win = targetWindow as WindowWithWebpackPublicPath;
  const assetPath = win.emui?._ASSET_PATH;
  if (!assetPath) return;

  win.__webpack_public_path__ = new URL('../', assetPath).href;
};

/**
 * Derives webpack public path from the guest hostUrl when _ASSET_PATH is not yet available.
 * @param {Window | null | undefined} [targetWindow] - Guest document defaultView.
 * @param {string} [hostUrl] - Guest application base URL from load options.
 */
export const syncWebpackPublicPathFromHost = (
  targetWindow?: Window | null,
  hostUrl?: string,
): void => {
  if (!targetWindow || !hostUrl) return;

  try {
    const win = targetWindow as WindowWithWebpackPublicPath;
    win.__webpack_public_path__ = new URL('./', hostUrl).href;
  } catch {
    // ignore invalid hostUrl
  }
};

/**
 * Prefers guest _ASSET_PATH when present, otherwise falls back to hostUrl.
 * @param {Window | null | undefined} [targetWindow] - Guest document defaultView.
 * @param {string} [hostUrl] - Guest application base URL from load options.
 * @returns {void}
 */
export const syncWebpackPublicPath = (
  targetWindow?: Window | null,
  hostUrl?: string,
): void => {
  if (!targetWindow) return;

  const win = targetWindow as WindowWithWebpackPublicPath;
  if (win.emui?._ASSET_PATH) {
    syncWebpackPublicPathFromAssetPath(targetWindow);
    return;
  }

  syncWebpackPublicPathFromHost(targetWindow, hostUrl);
};
